import { useCallback, useRef } from 'react'
import { disableScrollSnap, pinElementToViewport } from '../utils/unifiedScroll'

/**
 * FAQ open/close + pin question to top of viewport.
 *
 * Root-cause fix (2026-08-11): do not use smooth accordionScrollTo that
 * re-enables mandatory scroll-snap mid-section (caused up-then-down bounce).
 * Soft-snap while FAQ is in view is set by FAQSection (data-faq-section-active).
 * This hook: expand → wait layout stable → instant pin of question row.
 *
 * @param {number|null} expandedIndex
 * @param {Function} setExpandedIndex
 * @returns {Function} (index, event) => void
 */
export function useFAQScroll(expandedIndex, setExpandedIndex) {
  const generationRef = useRef(0)

  const toggleWithScroll = useCallback((index, event) => {
    const wasOpen = expandedIndex === index
    const clickedElement = event?.target?.closest?.('[data-testid^="faq-"]')
    if (!clickedElement) return

    const generation = ++generationRef.current
    setExpandedIndex(wasOpen ? null : index)

    if (wasOpen) return

    // Full snap off during expand so mandatory cannot fight layout growth
    disableScrollSnap()

    const faqSection = document.querySelector('[data-testid="faq-section"]')
    const questionContainer = clickedElement.closest('[id^="faq-"]')
    if (!faqSection || !questionContainer) return

    const startedAt = performance.now()
    const minWaitMs = 500 // AccordionWrap default duration
    const maxWaitMs = 2000

    const waitForStable = () =>
      new Promise((resolve) => {
        let stableFrames = 0
        let lastHeight = -1

        const tick = () => {
          if (generation !== generationRef.current) {
            resolve(false)
            return
          }

          const height = faqSection.getBoundingClientRect().height
          const elapsed = performance.now() - startedAt

          if (Math.abs(height - lastHeight) < 1) {
            stableFrames += 1
            if (stableFrames >= 6 && elapsed >= minWaitMs) {
              resolve(true)
              return
            }
          } else {
            stableFrames = 0
          }

          lastHeight = height

          if (elapsed >= maxWaitMs) {
            resolve(true)
            return
          }

          requestAnimationFrame(tick)
        }

        requestAnimationFrame(tick)
      })

    waitForStable().then((ok) => {
      if (!ok || generation !== generationRef.current) return
      if (!document.contains(questionContainer)) return
      pinElementToViewport(questionContainer, { offset: 80, duration: 800 })
    })
  }, [expandedIndex, setExpandedIndex])

  return toggleWithScroll
}
